/**
 * Template Preview Component
 *
 * Shows all ticket templates organized by group and key.
 * Useful for developers to see available templates.
 *
 * Usage:
 * ```tsx
 * import { TemplatePreview } from '@/modules/Ticket/templates/components/template-preview'
 * <TemplatePreview />
 * ```
 */

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, FileText, Settings } from 'lucide-react'
import { Typogaphy } from 'vnetwork-ui'
import Paper from '@/components/Paper'
import { GROUP_TEMPLATES } from '../group-templates'
import { KEY_OVERRIDES } from '../key-overrides'
import { SAMPLE_CONTENT } from '../sample-content'
import { TicketTypeGroup } from '../../types/response'

interface TemplateItemProps {
    title: string
    content: string
    defaults?: Record<string, unknown>
    type: 'group' | 'key'
}

const TemplateItem: React.FC<TemplateItemProps> = ({
    title,
    content,
    defaults,
    type,
}) => {
    const [expanded, setExpanded] = useState(false)

    return (
        <div className="border border-gray-200 rounded-md overflow-hidden">
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
                <div className="flex items-center gap-2">
                    {type === 'group' ? (
                        <Settings className="w-4 h-4 text-blue-500" />
                    ) : (
                        <FileText className="w-4 h-4 text-green-500" />
                    )}
                    <span className="font-medium text-sm">{title}</span>
                    <span
                        className={`text-xs px-2 py-0.5 rounded ${
                            type === 'group'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-green-100 text-green-700'
                        }`}
                    >
                        {type}
                    </span>
                </div>
                {expanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                )}
            </button>
            {expanded && (
                <div className="p-3 space-y-3">
                    {defaults && Object.keys(defaults).length > 0 && (
                        <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">
                                Defaults:
                            </p>
                            <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                                {JSON.stringify(defaults, null, 2)}
                            </pre>
                        </div>
                    )}
                    {content && (
                        <div>
                            <p className="text-xs font-semibold text-gray-500 mb-1">
                                Sample Content:
                            </p>
                            <div className="text-xs bg-gray-50 p-3 rounded border border-gray-100 whitespace-pre-wrap font-mono max-h-[300px] overflow-y-auto">
                                {content}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export const TemplatePreview: React.FC = () => {
    const { t } = useTranslation()
    const [activeGroup, setActiveGroup] = useState<string | null>(null)

    const groups = Object.keys(GROUP_TEMPLATES) as TicketTypeGroup[]

    // Infer group from key prefix
    const getGroupFromKey = (key: string): string => {
        if (key.startsWith('error_service')) return 'error_service'
        if (key.startsWith('service_')) return 'service'
        if (key.startsWith('support_')) return 'support_request'
        if (key.startsWith('meeting')) return 'meeting'
        if (key.startsWith('internal')) return 'internal'
        if (key.startsWith('change')) return 'change'
        return 'unknown'
    }

    // Get key overrides organized by group
    const keysByGroup = Object.keys(KEY_OVERRIDES).reduce(
        (acc, key) => {
            const group = getGroupFromKey(key)
            if (!acc[group]) acc[group] = []
            acc[group].push(key)
            return acc
        },
        {} as Record<string, string[]>
    )

    return (
        <Paper className="p-4">
            <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-primary" />
                <Typogaphy className="text-lg font-semibold">
                    Ticket Templates Overview
                </Typogaphy>
            </div>

            <div className="space-y-4">
                {groups.map((group) => {
                    const template = GROUP_TEMPLATES[group]
                    const keys = keysByGroup[group] || []
                    const isActive = activeGroup === group

                    return (
                        <div
                            key={group}
                            className="border border-gray-200 rounded-lg overflow-hidden"
                        >
                            {/* Group Header */}
                            <button
                                onClick={() =>
                                    setActiveGroup(isActive ? null : group)
                                }
                                className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                        <Settings className="w-4 h-4 text-primary" />
                                    </div>
                                    <div className="text-left">
                                        <p className="font-semibold">
                                            {t(
                                                `ticket-type-group-${group.replace(/_/g, '-')}`
                                            )}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {group} • {keys.length} key
                                            overrides
                                        </p>
                                    </div>
                                </div>
                                {isActive ? (
                                    <ChevronDown className="w-5 h-5 text-gray-400" />
                                ) : (
                                    <ChevronRight className="w-5 h-5 text-gray-400" />
                                )}
                            </button>

                            {/* Group Content */}
                            {isActive && (
                                <div className="p-4 bg-gray-50 border-t border-gray-200 space-y-3">
                                    {/* Base Group Template */}
                                    <TemplateItem
                                        title={`Base: ${group}`}
                                        content={
                                            SAMPLE_CONTENT[`group:${group}`] ||
                                            ''
                                        }
                                        defaults={template.defaults}
                                        type="group"
                                    />

                                    {/* Key Overrides */}
                                    {keys.length > 0 && (
                                        <div className="mt-3">
                                            <p className="text-xs font-semibold text-gray-500 mb-2">
                                                Key Overrides:
                                            </p>
                                            <div className="space-y-2">
                                                {keys.map((key) => (
                                                    <TemplateItem
                                                        key={key}
                                                        title={key}
                                                        content={
                                                            SAMPLE_CONTENT[
                                                                `key:${key}`
                                                            ] || ''
                                                        }
                                                        defaults={
                                                            KEY_OVERRIDES[key]
                                                                ?.defaults
                                                        }
                                                        type="key"
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            {/* Quick Reference */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-sm font-semibold text-blue-700 mb-2">
                    Quick Reference
                </p>
                <ul className="text-xs text-blue-600 space-y-1">
                    <li>
                        • <strong>Group Template:</strong> Base configuration
                        for each ticket type group
                    </li>
                    <li>
                        • <strong>Key Override:</strong> Specific overrides for
                        individual ticket_type_key
                    </li>
                    <li>
                        • <strong>Sample Content:</strong> Pre-filled content
                        when selecting ticket type
                    </li>
                    <li>
                        • <strong>Defaults:</strong> Default field values
                        applied automatically
                    </li>
                </ul>
            </div>
        </Paper>
    )
}

export default TemplatePreview
